/**
 * ParameterHistoryStrip — compact vertical timeline showing all historical
 * values for a reference quantity. Each row is selectable so the user can
 * pick which S_ref values to overlay on the plot comparison.
 */

import { Text } from "@radix-ui/themes";
import type { RefQuantityHistoryEntry } from "../../api/referenceQuantities";

interface Props {
  history: RefQuantityHistoryEntry[];
  currentValue: number | null;
  selectedValues: Set<number>;
  onToggleValue: (value: number) => void;
  units: string | null;
  isLight: boolean;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function deduplicateByValue(
  entries: RefQuantityHistoryEntry[],
): RefQuantityHistoryEntry[] {
  const seen = new Set<number | null>();
  return entries.filter((e) => {
    if (seen.has(e.value)) return false;
    seen.add(e.value);
    return true;
  });
}

export function ParameterHistoryStrip({
  history,
  currentValue,
  selectedValues,
  onToggleValue,
  units,
  isLight,
}: Props) {
  const unique = deduplicateByValue(history);
  if (unique.length < 2) return null;

  const u = units ?? "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Text size="1" weight="medium" style={{ opacity: 0.6, marginBottom: 2 }}>
        Value history
      </Text>
      {unique.map((entry, i) => {
        if (entry.value == null) return null;
        const isCurrent = entry.value === currentValue;
        const isSelected = selectedValues.has(entry.value);

        return (
          <button
            key={`${entry.changed_at}-${i}`}
            onClick={() => onToggleValue(entry.value!)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 8px",
              borderRadius: 4,
              border: isSelected
                ? `1.5px solid ${isLight ? "#00643C" : "#4ade80"}`
                : `1px solid ${isLight ? "#e0e0e0" : "#333"}`,
              background: isSelected
                ? (isLight ? "#e8f5ee" : "#0a2818")
                : "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 11,
              color: isLight ? "#111" : "#e0e0e0",
              textAlign: "left",
              width: "100%",
              transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: isCurrent
                  ? (isLight ? "#00643C" : "#4ade80")
                  : (isLight ? "#aaa" : "#555"),
                flexShrink: 0,
              }}
            />
            <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", minWidth: 70 }}>
              {entry.value}{u}
            </span>
            <span style={{ flex: 1, opacity: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {entry.changed_by}
            </span>
            <span style={{ opacity: 0.4, whiteSpace: "nowrap", flexShrink: 0 }}>
              {entry.changed_at ? formatDate(entry.changed_at) : ""}
            </span>
            {isCurrent && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  padding: "0 4px",
                  borderRadius: 3,
                  background: isLight ? "#00643C" : "#4ade80",
                  color: isLight ? "#fff" : "#0a0a0a",
                  flexShrink: 0,
                }}
              >
                current
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
